/**
 * AI Provider Service Type Definitions
 * 
 * This file defines all interfaces and types for the provider abstraction layer.
 * These types enable a consistent API across multiple AI providers (OpenAI, Anthropic, Google, xAI).
 */

/**
 * Supported AI providers
 */
export enum AIProvider {
  OPENAI = 'openai',
  ANTHROPIC = 'anthropic',
  GOOGLE = 'google',
  XAI = 'xai'
}

/**
 * Speed rating for models - used for UI display and selection guidance
 */
export type ModelSpeed = 'very-fast' | 'fast' | 'balanced';

/**
 * Model capabilities that can vary by model
 */
export type ModelCapability = 
  | 'vision'
  | 'function_calling'
  | 'json_mode'
  | 'tool_use'
  | 'reasoning'
  | 'extended_thinking';

/**
 * Configuration for an AI model
 */
export interface AIModelConfig {
  /** Unique identifier for the model (e.g., 'gpt-4o', 'claude-sonnet-4.5') */
  id: string;
  /** API model name (may differ from id, e.g., 'claude-sonnet-4-5-20260115') */
  name: string;
  /** Which provider this model belongs to */
  provider: AIProvider;
  /** Human-readable display name */
  displayName: string;
  /** Maximum context window in tokens */
  maxTokens: number;
  /** Maximum output tokens (completion) */
  maxOutputTokens?: number;
  /** Whether this is a small/compact model (used for large prompt safeguards) */
  isSmall?: boolean;
  /** Model ID to upgrade to when prompt is large */
  upgradeModelId?: string;
  /** Whether the model supports JSON mode/structured output */
  supportsJsonMode: boolean;
  /** Whether the model supports streaming responses */
  supportsStreaming: boolean;
  /** Speed rating for UI display */
  speed: ModelSpeed;
  /** Whether this is a recommended model for the provider */
  isRecommended: boolean;
  /** List of capabilities this model supports */
  capabilities: ModelCapability[];
}

/**
 * Options for making an AI request
 */
export interface AIRequestOptions {
  /** Model ID to use for the request */
  model: string;
  /** Temperature for response randomness (0-2 for most providers) */
  temperature?: number;
  /** Maximum tokens in the response */
  maxTokens?: number;
  /** Whether to enable JSON mode for structured responses */
  jsonMode?: boolean;
  /** System prompt to set context for the conversation */
  systemPrompt?: string;
}

/**
 * Message role types (aligned with OpenAI format as base)
 */
export type AIMessageRole = 'system' | 'user' | 'assistant';

/**
 * A message in the conversation
 */
export interface AIMessage {
  /** Role of the message sender */
  role: AIMessageRole;
  /** Content of the message */
  content: string;
}

/**
 * Token usage statistics from a response
 */
export interface AIUsage {
  /** Tokens used in the prompt */
  promptTokens: number;
  /** Tokens used in the completion */
  completionTokens: number;
  /** Total tokens used */
  totalTokens: number;
}

/**
 * Normalized response from any AI provider
 */
export interface AIResponse {
  /** The generated content */
  content: string;
  /** Model that generated the response */
  model: string;
  /** Provider that generated the response */
  provider: AIProvider;
  /** Token usage statistics */
  usage: AIUsage;
  /** Reason the generation finished (e.g., 'stop', 'length') */
  finishReason: string;
}

/**
 * Interface that all AI providers must implement
 */
export interface IAIProvider {
  /** The provider type */
  readonly provider: AIProvider;
  
  /**
   * Generate a completion from the AI model
   * @param messages - Conversation messages
   * @param options - Request options including model and temperature
   * @returns Normalized AI response
   */
  generateCompletion(messages: AIMessage[], options: AIRequestOptions): Promise<AIResponse>;
  
  /**
   * Check if this provider is available (has valid credentials)
   * @returns Whether the provider can be used
   */
  isAvailable(): Promise<boolean>;
  
  /**
   * Get the list of models available from this provider
   * @returns Array of model configurations
   */
  getAvailableModels(): AIModelConfig[];
}

/**
 * Provider-specific error information
 */
export interface AIProviderError {
  /** Provider that threw the error */
  provider: AIProvider;
  /** Error code from the provider */
  code: string;
  /** Human-readable error message */
  message: string;
  /** Original error from the provider API */
  originalError?: unknown;
}

/**
 * Authentication methods supported by providers
 */
export type AuthMethod = 'bearer' | 'x-api-key' | 'query-param';

/**
 * Provider configuration
 */
export interface ProviderConfig {
  /** API endpoint URL */
  endpoint: string;
  /** Authentication method */
  authMethod: AuthMethod;
  /** Forge resolver function name for getting the API token */
  tokenResolver: string;
}
